import React, { useContext, useState, MouseEvent, FC } from 'react'
import { StyledChangePassword } from './ChangePassword.styled'
import { useSelector } from 'react-redux'
import { AppState, Product } from '@news-mono/web-common'
import { UserSettingsLink } from '../components/UserSettingsLink'
import { MyAccountContext } from '../hooks/useMyAccountContext'
import { UserSettingsInput } from '../components/UserSettingsInput'
import { metrics } from '../../__styling'
import { UserSettingsButton } from '../components/UserSettingsButton'
import {
    useChangePassword,
    useForgotPassword,
    validatePassword,
} from '../hooks/useChangePassword'
import { UserSettingProps } from '../UserSetting'
import { StyledButtonGroup } from '../components/UserSettingsButtonGroup.styled'
import { useProduct } from '../../__product'
import { tokens } from '@news-mono/design-tokens'
import { useDebugQueryParams } from '../hooks/useDebugQueryParams'

// TheNightly Colors
const tnColors = tokens.thenightly.colors
const tnPalette = tnColors.palette
// TheNightly Metrics
const tnMetrics = metrics.thenightly
const tnMargins = tnMetrics.margins

// PerthNow Colors
const pnColors = tokens.perthnow.colors
const pnPalette = pnColors.palette

export interface ChangePasswordForm {
    currentPassword: string
    newPassword: string
}
export interface ChangePasswordProps extends UserSettingProps {}

export const ChangePassword: FC<ChangePasswordProps> = ({ config }) => {
    const { wanUserId, auth0UserId, userEmail } = useSelector(
        (state: AppState) => state.authentication,
    )
    const { setToast, setMessageBanner, goBackToParent } =
        useContext(MyAccountContext)
    const [password, setPassword] = useState<ChangePasswordForm>({
        currentPassword: '',
        newPassword: '',
    })
    const [submitting, setSubmitting] = useState<boolean>(false)

    // Product Handlers
    const product = useProduct()
    const isPn = product === Product.PerthNow

    // Auth0 Handlers
    const { mutateAsync: changePassword } = useChangePassword({
        userId: auth0UserId || wanUserId,
        currentPassword: password.currentPassword,
        newPassword: password.newPassword,
        email: userEmail!,
        passEncryptionKey: config.auth0!.passEncryptionKey!,
    })
    const { mutateAsync: sendForgotPasswordEmail } = useForgotPassword({
        email: userEmail!,
        isPn,
    })

    // Debug Handlers
    const debugParams = useDebugQueryParams()
    const isFakeComplete = debugParams.fakeComplete

    async function onSubmit(e: MouseEvent<HTMLElement>) {
        e.preventDefault()
        setMessageBanner(undefined)
        if (submitting) return
        const validate = validatePassword(password)
        if (!validate.valid && !isFakeComplete) {
            validate.message &&
                setMessageBanner({
                    text: validate.message,
                    type: 'error',
                })
        } else {
            setSubmitting(true)
            const res = await changePassword()
            setSubmitting(false)
            if (res.result || isFakeComplete) {
                setToast({
                    text: res.message,
                    type: 'success',
                    show: true,
                })
                goBackToParent(false)
            } else {
                setMessageBanner({
                    type: 'error',
                    text: res.message,
                })
            }
        }
    }

    async function onForgotPassword() {
        setMessageBanner(undefined)
        const res = await sendForgotPasswordEmail()
        if (res.result || isFakeComplete) {
            setToast({
                text: res.message,
                type: 'success',
                show: true,
            })
        } else {
            setMessageBanner({
                type: 'error',
                text: res.message,
            })
        }
    }

    function onCancel(e: MouseEvent<HTMLElement>) {
        e.preventDefault()
        goBackToParent(true)
    }

    return (
        <StyledChangePassword>
            <UserSettingsInput
                id="current-password"
                label="Current Password"
                type="password"
                onChange={(currentPassword) =>
                    setPassword({ ...password, currentPassword })
                }
                marginBottom={tnMargins.md}
                autoFocus={true}
            />
            <UserSettingsLink
                width={'fit-content'}
                onClick={onForgotPassword}
                color={
                    isPn
                        ? pnPalette.pinkThulite
                        : tnPalette.section.opinion.default
                }
            >
                Forgot your password{isPn ? '?' : ''}
            </UserSettingsLink>
            <UserSettingsInput
                id="new-password"
                label="New password"
                type="password"
                onChange={(newPassword) =>
                    setPassword({ ...password, newPassword })
                }
                marginBottom={32}
            />
            <StyledButtonGroup>
                <UserSettingsButton onClick={onCancel} btnType="muted">
                    CANCEL
                </UserSettingsButton>
                <UserSettingsButton
                    onClick={onSubmit}
                    btnType="primary"
                    disabled={submitting}
                >
                    SAVE
                </UserSettingsButton>
            </StyledButtonGroup>
        </StyledChangePassword>
    )
}
